/**
 * @fileoverview Input/output schemas for divergence-report-v2.
 *
 * This agent analyzes structural divergences between the author's planned story
 * and what was actually written. It receives pre-computed comparison data and
 * applies editorial judgment to identify significant divergences.
 *
 * The output is optimized for downstream consumption by structural-suggestions-v1,
 * which uses the divergence findings to propose concrete structural changes.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 * Reports are ephemeral — not persisted to project state.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * All comparison data is pre-computed by the consumer hook.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.plannedPlot.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.actualPlotProgression.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storylineDivergenceData.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characterDivergenceData.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.plotThreadStatus.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.projectedWordCount.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyType.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

const PlotDivergenceSchema = z.object({
  type: z.enum(['planned_not_occurred', 'unplanned_occurred', 'reordered', 'modified']).describe(
    'The type of plot divergence.'
  ),
  description: z.string().describe('What diverged — specific and concrete.'),
  significance: z.enum(['moderate', 'major']).describe('How impactful this divergence is.'),
  chapterRange: z.string().describe('Where in the story this occurs (e.g., "Chapters 3-5").'),
  recommendation: z.enum(['update_plan', 'steer_story', 'author_decision']).describe(
    'Suggested action: update the plan to match reality, steer the story back, or leave it to the author.'
  ),
  affectedStorylines: z.array(z.string()).describe(
    'Names of storylines affected by this plot divergence. Empty array if no specific storyline is affected.'
  ),
});

const StorylineDivergenceSchema = z.object({
  storylineName: z.string().describe('The name of the storyline that diverged.'),
  storylineIndex: z.number().describe('Index of the storyline in the existingStorylines array (0-based).'),
  type: z.enum(['trajectory_shift', 'stalled', 'abandoned', 'accelerated']).describe(
    'The type of storyline divergence.'
  ),
  description: z.string().describe('What specifically diverged in this storyline.'),
  significance: z.enum(['moderate', 'major']).describe('How impactful this divergence is.'),
  affectedPhases: z.array(z.string()).describe(
    'The structural phases where divergence is observed (e.g., ["Midpoint", "Break Into Three"]). '
    + 'Use the 7-phase model: Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State.'
  ),
  affectedBeatIds: z.array(z.string()).describe(
    'IDs of specific beats affected by this divergence. Empty array if the divergence is at the storyline level '
    + '(e.g., a stalled storyline affects all its beats, not specific ones).'
  ),
  suggestedAction: z.enum([
    'move_beats',
    'add_beats',
    'remove_beats',
    'change_priority',
    'restructure',
  ]).describe(
    'The structural action that would best address this divergence. '
    + 'move_beats: beats exist but are in the wrong phase. '
    + 'add_beats: gaps in the storyline need new beats. '
    + 'remove_beats: beats describe events that will not happen. '
    + 'change_priority: storyline importance shifted (A→B, C→B, etc). '
    + 'restructure: multiple changes needed (combination of moves, adds, removes).'
  ),
  evidenceChapters: z.array(z.number()).describe(
    'Chapter numbers that provide evidence for this divergence.'
  ),
});

const CharacterDivergenceSchema = z.object({
  characterName: z.string().describe('The name of the character whose arc diverged.'),
  type: z.enum(['arc_diverged', 'role_shifted', 'absent', 'elevated']).describe(
    'The type of character arc divergence.'
  ),
  description: z.string().describe('What specifically diverged in this character\'s arc.'),
  significance: z.enum(['moderate', 'major']).describe('How impactful this divergence is.'),
});

const ThreadDivergenceSchema = z.object({
  threadTitle: z.string().describe('The title of the plot thread.'),
  type: z.enum(['overdue_resolution', 'premature_resolution', 'lost_thread', 'unplanned_thread']).describe(
    'The type of thread divergence.'
  ),
  description: z.string().describe('What specifically diverged with this thread.'),
});

/**
 * Output schema for divergence-report-v2.
 * Only moderate and major significance — no cosmetic/minor items.
 *
 * Storyline divergences are enriched with beat-level references, affected phases,
 * and suggested actions to enable downstream structural suggestion generation.
 */
export const OutputSchema = z.object({
  summary: z.object({
    verdict: z.enum(['on_track', 'minor_drift', 'significant_drift', 'major_divergence']).describe(
      'Overall assessment of how far the written story has diverged from the plan.'
    ),
    overview: z.string().describe('A 2-3 sentence summary of the divergence state.'),
  }),

  plotDivergences: z.array(PlotDivergenceSchema).describe(
    'Major plot-level divergences between the plan and what was written. Only include significant items.'
  ),

  storylineDivergences: z.array(StorylineDivergenceSchema).describe(
    'Per-storyline divergences with beat-level detail. Includes affected phases, beat IDs, and suggested '
    + 'structural actions. This is the primary input for downstream structural suggestion generation.'
  ),

  characterDivergences: z.array(CharacterDivergenceSchema).describe(
    'Per-character arc divergences: arcs that went differently than planned. Only include significant items.'
  ),

  threadDivergences: z.array(ThreadDivergenceSchema).describe(
    'Plot thread timing issues: overdue resolutions, lost threads, unplanned threads.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type PlotDivergence = z.infer<typeof PlotDivergenceSchema>;
export type StorylineDivergence = z.infer<typeof StorylineDivergenceSchema>;
export type CharacterDivergence = z.infer<typeof CharacterDivergenceSchema>;
export type ThreadDivergence = z.infer<typeof ThreadDivergenceSchema>;
